import { useAuthStore } from './auth';

const API_BASE = import.meta.env.VITE_API_URL || 'https://group-booking-api.georgec520w.workers.dev';

async function request<T>(endpoint: string, options?: RequestInit): Promise<T> {
  // Get auth token from store
  const token = useAuthStore.getState().token;

  const res = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...options?.headers,
    },
  });

  if (res.status === 401) {
    // Token expired or invalid — clear auth so ProtectedRoute redirects to /login on next render
    useAuthStore.getState().logout();
    throw new Error('Unauthorized');
  }

  if (!res.ok) {
    // Surface the server's reason (plain error string or zod validation issues) instead of a bare status code
    let detail = '';
    try {
      const body = await res.json();
      if (typeof body?.error === 'string') {
        detail = body.error;
      } else if (Array.isArray(body?.error?.issues)) {
        detail = body.error.issues
          .map((i: { path?: (string | number)[]; message?: string }) => `${(i.path || []).join('.')}: ${i.message}`)
          .join('; ');
      } else if (typeof body?.message === 'string') {
        detail = body.message;
      }
    } catch {
      // non-JSON body; fall through
    }
    throw new Error(`API error: ${res.status}${detail ? ` (${detail})` : ''}`);
  }

  return res.json();
}

type Paged<T> = { success: boolean; data: T[]; pagination?: { page: number; totalPages: number } };

/** The API pages at 100 rows; the admin wants the whole list, so walk every page. */
async function requestAll<T = any>(path: string): Promise<Paged<T>> {
  const sep = path.includes('?') ? '&' : '?';
  const first = await request<Paged<T>>(`${path}${sep}pageSize=100`);
  const data = [...(first.data || [])];
  const totalPages = first.pagination?.totalPages || 1;
  for (let page = 2; page <= totalPages; page++) {
    const next = await request<Paged<T>>(`${path}${sep}pageSize=100&page=${page}`);
    data.push(...(next.data || []));
  }
  return { ...first, data };
}

export const api = {
  // Events
  getEvents: () => requestAll('/api/events'),
  getEvent: (id: string) => request<{ success: boolean; data: any }>(`/api/events/${id}`),
  createEvent: (data: any) => request<{ success: boolean; data: any }>('/api/events', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  updateEvent: (id: string, data: any) => request<{ success: boolean; data: any }>(`/api/events/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  deleteEvent: (id: string) => request<{ success: boolean }>(`/api/events/${id}`, {
    method: 'DELETE',
  }),

  // Registrations
  getRegistrations: (eventId?: string) => {
    const url = eventId ? `/api/registrations?eventId=${eventId}` : '/api/registrations';
    return requestAll(url);
  },
  getRegistration: (id: string) => request<{ success: boolean; data: any }>(`/api/registrations/${id}`),
  updateRegistration: (id: string, data: any) => request<{ success: boolean; data: any }>(`/api/registrations/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  }),
  deleteRegistration: (id: string) => request<{ success: boolean }>(`/api/registrations/${id}`, {
    method: 'DELETE',
  }),

  // Stats
  getStats: () => request<{ success: boolean; data: any }>('/api/stats'),
  getEventStats: (eventId: string) => request<{ success: boolean; data: any }>(`/api/stats/events/${eventId}`),

  // Themes
  getThemes: () => request<{ success: boolean; data: { system: any[]; custom: any[] } }>('/api/themes'),

  // Event Modules
  addModule: (eventId: string, module: any) => request<{ success: boolean; data: any }>(`/api/events/${eventId}/modules`, {
    method: 'POST',
    body: JSON.stringify(module),
  }),
  updateModule: (eventId: string, moduleId: string, module: any) => request<{ success: boolean; data: any }>(`/api/events/${eventId}/modules/${moduleId}`, {
    method: 'PUT',
    body: JSON.stringify(module),
  }),
  deleteModule: (eventId: string, moduleId: string) => request<{ success: boolean }>(`/api/events/${eventId}/modules/${moduleId}`, {
    method: 'DELETE',
  }),

  // Upload
  uploadQrCode: async (file: File, eventId?: string): Promise<{ success: boolean; data?: { url: string } }> => {
    const token = useAuthStore.getState().token;
    const formData = new FormData();
    formData.append('file', file);
    if (eventId) formData.append('eventId', eventId);

    const res = await fetch(`${API_BASE}/api/upload/qrcode`, {
      method: 'POST',
      body: formData,
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    });

    if (res.status === 401) {
      useAuthStore.getState().logout();
      throw new Error('Unauthorized');
    }
    if (!res.ok) {
      throw new Error(`Upload error: ${res.status}`);
    }

    return res.json();
  },

  uploadBanner: async (file: File, eventId?: string): Promise<{ success: boolean; data?: { url: string } }> => {
    const token = useAuthStore.getState().token;
    const formData = new FormData();
    formData.append('file', file);
    if (eventId) formData.append('eventId', eventId);

    const res = await fetch(`${API_BASE}/api/upload/banner`, {
      method: 'POST',
      body: formData,
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    });

    if (res.status === 401) {
      useAuthStore.getState().logout();
      throw new Error('Unauthorized');
    }
    if (!res.ok) {
      throw new Error(`Upload error: ${res.status}`);
    }

    return res.json();
  },
};
